/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit, Mail, Phone, Building2, Shield, Loader2, Save, Check, Star, Send, Award, X, UserPlus, Search, Upload, FileText, Trash2, Eye, Download, Printer, FileCheck, ScrollText, ShieldCheck, Calendar, Hash, MapPin, Globe, Users, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DocumentsMembreGED } from './DocumentsMembreGED';
import { ExpertsRattaches } from './ExpertsRattaches';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { formatCodeNAF } from '../../utils/codeNaf';

const API = process.env.REACT_APP_BACKEND_URL;

export const EditContactModal = ({ membre, open, onClose, onSave, token, userRole }) => {
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [entreprise, setEntreprise] = useState('');
  const [siret, setSiret] = useState('');
  const [codeNaf, setCodeNaf] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [adresse, setAdresse] = useState('');
  const [codePostal, setCodePostal] = useState('');
  const [ville, setVille] = useState('');
  const [pays, setPays] = useState('');
  const [territoire, setTerritoire] = useState('');
  const [statutJuridique, setStatutJuridique] = useState('');
  const [dateCreation, setDateCreation] = useState('');
  const [effectif, setEffectif] = useState('');
  const [chiffreAffaires, setChiffreAffaires] = useState('');
  const [siteWeb, setSiteWeb] = useState('');
  const [experiencePro, setExperiencePro] = useState('');
  const [loading, setSaving] = useState(false);

  const canEditAll = ['super_admin', 'admin', 'cooper'].includes(userRole);

  // Resynchroniser les champs quand le membre change ou que le modal s'ouvre
  useEffect(() => {
    if (membre && open) {
      setEmail(membre.email || '');
      setTelephone(membre.telephone || '');
      setEntreprise(membre.entreprise || '');
      setSiret(membre.siret || '');
      setCodeNaf(membre.code_naf || '');
      setNom(membre.nom || '');
      setPrenom(membre.prenom || '');
      setAdresse(membre.adresse || '');
      setCodePostal(membre.code_postal || '');
      setVille(membre.ville || '');
      setPays(membre.pays || '');
      setTerritoire(membre.territoire || '');
      setStatutJuridique(membre.statut_juridique || '');
      setDateCreation(membre.date_creation_entreprise || '');
      setEffectif(membre.effectif || '');
      setChiffreAffaires(membre.chiffre_affaires || '');
      setSiteWeb(membre.site_web || '');
      setExperiencePro(
        membre.experience_professionnelle_annees != null
          ? String(membre.experience_professionnelle_annees)
          : '',
      );
    }
  }, [membre, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (entreprise !== (membre.entreprise || '')) payload.entreprise = entreprise;
      if (siret !== (membre.siret || '')) payload.siret = siret;
      if (codeNaf !== (membre.code_naf || '')) payload.code_naf = formatCodeNAF(codeNaf);
      if (email !== (membre.email || '')) payload.email = email;
      if (telephone !== (membre.telephone || '')) payload.telephone = telephone;
      if (nom !== (membre.nom || '')) payload.nom = nom;
      if (prenom !== (membre.prenom || '')) payload.prenom = prenom;
      if (adresse !== (membre.adresse || '')) payload.adresse = adresse;
      if (codePostal !== (membre.code_postal || '')) payload.code_postal = codePostal;
      if (ville !== (membre.ville || '')) payload.ville = ville;
      if (pays !== (membre.pays || '')) payload.pays = pays;
      if (territoire !== (membre.territoire || '')) payload.territoire = territoire;
      if (statutJuridique !== (membre.statut_juridique || '')) payload.statut_juridique = statutJuridique;
      if (dateCreation !== (membre.date_creation_entreprise || '')) payload.date_creation_entreprise = dateCreation;
      if (effectif !== (membre.effectif || '')) payload.effectif = effectif;
      if (chiffreAffaires !== (membre.chiffre_affaires || '')) payload.chiffre_affaires = chiffreAffaires;
      if (siteWeb !== (membre.site_web || '')) payload.site_web = siteWeb;
      // Expérience professionnelle (entier ≥ 0). On envoie `null` si vide pour
      // que le membre puisse retirer la valeur.
      const currExp = membre.experience_professionnelle_annees != null
        ? String(membre.experience_professionnelle_annees) : '';
      if (experiencePro !== currExp) {
        const parsed = experiencePro === '' ? null : Math.max(0, parseInt(experiencePro, 10) || 0);
        payload.experience_professionnelle_annees = parsed;
      }

      if (Object.keys(payload).length === 0) {
        toast.info('Aucune modification detectee');
        onClose();
        return;
      }

      const response = await axios.put(
        `${API}/api/admin/membres/${membre.id}/coordonnees`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Coordonnees mises a jour avec succes');
      onSave(response.data.membre || { ...membre, ...payload });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  if (!membre) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="edit-contact-modal">
        {/* Header gradient pro */}
        <div className="px-6 pt-5 pb-4 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 border-b border-amber-100">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-3 text-base">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-md flex-shrink-0">
                <Edit className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-gray-900 leading-tight">Modifier les coordonnées</p>
                <p className="text-xs text-gray-600 mt-0.5 truncate">
                  Édition des coordonnées de <span className="font-semibold text-gray-800">{membre.prenom || ''} {membre.nom || membre.entreprise || ''}</span>
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 pb-4 space-y-5 py-4">
          {/* Identité */}
          <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/30 to-white overflow-hidden">
            <div className="px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50/40 border-b border-blue-100 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">Identité</span>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-nom" className="text-xs font-semibold">Nom</Label>
                <Input id="edit-nom" data-testid="edit-nom-input" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom" className="h-9" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-prenom" className="text-xs font-semibold">Prénom</Label>
                <Input id="edit-prenom" data-testid="edit-prenom-input" value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="Prénom" className="h-9" />
              </div>
            </div>
          </div>

          {/* Entreprise */}
          <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/30 to-white overflow-hidden">
            <div className="px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-purple-50/40 border-b border-indigo-100 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-600" />
              <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Entreprise</span>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-entreprise" className="text-xs font-semibold">Raison sociale</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="edit-entreprise" data-testid="edit-entreprise-input" value={entreprise} onChange={(e) => setEntreprise(e.target.value)} className="pl-10 h-9" placeholder="Raison sociale" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-statut-juridique" className="text-xs font-semibold">Statut juridique</Label>
                  <Input id="edit-statut-juridique" data-testid="edit-statut-juridique-input" value={statutJuridique} onChange={(e) => setStatutJuridique(e.target.value)} className="h-9" placeholder="SAS, SARL, EI..." />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-date-creation" className="text-xs font-semibold flex items-center justify-between gap-2">
                    <span>Date de création</span>
                    {(() => {
                      // Calcul automatique du nombre d'années d'existence à
                      // partir de la date de création (formats acceptés :
                      // ISO YYYY-MM-DD ou FR JJ/MM/AAAA). Affiché en badge à
                      // droite du label dès qu'une date valide est saisie.
                      if (!dateCreation) return null;
                      const raw = dateCreation.trim();
                      let parsed = null;
                      const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      const frMatch = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
                      if (isoMatch) {
                        parsed = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`);
                      } else if (frMatch) {
                        parsed = new Date(`${frMatch[3]}-${frMatch[2]}-${frMatch[1]}`);
                      }
                      if (!parsed || isNaN(parsed.getTime())) return null;
                      const years = Math.floor((Date.now() - parsed.getTime()) / (365.25 * 24 * 3600 * 1000));
                      if (years < 0) return null;
                      return (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-indigo-700 bg-indigo-100 border border-indigo-200"
                          data-testid="annees-existence-badge"
                          title="Calculé automatiquement et repris dans le simulateur"
                        >
                          <Clock className="h-2.5 w-2.5" />
                          {years} an{years > 1 ? 's' : ''}
                        </span>
                      );
                    })()}
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="edit-date-creation" data-testid="edit-date-creation-input" value={dateCreation} onChange={(e) => setDateCreation(e.target.value)} className="pl-10 h-9" placeholder="01/01/2020 ou 2020-01-01" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-effectif" className="text-xs font-semibold">Effectif</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="edit-effectif" data-testid="edit-effectif-input" value={effectif} onChange={(e) => setEffectif(e.target.value)} className="pl-10 h-9" placeholder="12 salariés" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-ca" className="text-xs font-semibold">Chiffre d'affaires</Label>
                  <div className="relative">
                    <Star className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="edit-ca" data-testid="edit-ca-input" type="number" value={chiffreAffaires} onChange={(e) => setChiffreAffaires(e.target.value)} className="pl-10 h-9" placeholder="150000" />
                  </div>
                </div>
              </div>
              {/* Expérience Professionnelle — repris dynamiquement dans le simulateur d'éligibilité */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-experience-pro" className="text-xs font-semibold flex items-center gap-1.5">
                    Expérience Professionnelle
                    <span className="text-[10px] font-normal text-indigo-600 italic">(repris dans le simulateur)</span>
                  </Label>
                  <div className="relative">
                    <Award className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="edit-experience-pro"
                      data-testid="edit-experience-pro-input"
                      type="number"
                      min="0"
                      max="80"
                      step="1"
                      value={experiencePro}
                      onChange={(e) => setExperiencePro(e.target.value)}
                      className="pl-10 pr-12 h-9"
                      placeholder="5"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">années</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-siret" className="text-xs font-semibold">SIRET</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="edit-siret" data-testid="edit-siret-input" value={siret} onChange={(e) => setSiret(e.target.value)} className="pl-10 h-9" placeholder="123 456 789 00012" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-code-naf" className="text-xs font-semibold">Code APE / NAF</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="edit-code-naf" data-testid="edit-code-naf-input" value={codeNaf} onChange={(e) => setCodeNaf(e.target.value)} onBlur={() => setCodeNaf(formatCodeNAF(codeNaf))} className="pl-10 h-9" placeholder="41.20A" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/30 to-white overflow-hidden">
            <div className="px-4 py-2.5 bg-gradient-to-r from-emerald-50 to-teal-50/40 border-b border-emerald-100 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Adresse &amp; Territoire</span>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-adresse" className="text-xs font-semibold">Adresse</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input id="edit-adresse" data-testid="edit-adresse-input" value={adresse} onChange={(e) => setAdresse(e.target.value)} className="pl-10 h-9" placeholder="Rue, numéro..." />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-code-postal" className="text-xs font-semibold">Code Postal</Label>
                  <Input id="edit-code-postal" data-testid="edit-code-postal-input" value={codePostal} onChange={(e) => setCodePostal(e.target.value)} className="h-9" placeholder="97600" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-ville" className="text-xs font-semibold">Ville</Label>
                  <Input id="edit-ville" data-testid="edit-ville-input" value={ville} onChange={(e) => setVille(e.target.value)} className="h-9" placeholder="Mamoudzou" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-pays" className="text-xs font-semibold">Pays</Label>
                  <Input id="edit-pays" data-testid="edit-pays-input" value={pays} onChange={(e) => setPays(e.target.value)} className="h-9" placeholder="France" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-territoire" className="text-xs font-semibold">Territoire d'intervention</Label>
                  <Input id="edit-territoire" data-testid="edit-territoire-input" value={territoire} onChange={(e) => setTerritoire(e.target.value)} className="h-9" placeholder="MAYOTTE, GUADELOUPE..." />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-site-web" className="text-xs font-semibold">Site web</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input id="edit-site-web" data-testid="edit-site-web-input" value={siteWeb} onChange={(e) => setSiteWeb(e.target.value)} className="pl-10 h-9" placeholder="www.exemple.com" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/30 to-white overflow-hidden">
            <div className="px-4 py-2.5 bg-gradient-to-r from-blue-50 to-cyan-50/40 border-b border-blue-100 flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">Contact</span>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-email" className="text-xs font-semibold">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input id="edit-email" data-testid="edit-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-9" placeholder="email@exemple.com" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-telephone" className="text-xs font-semibold">Téléphone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input id="edit-telephone" type="tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} className="pl-10 h-9" placeholder="+590 690 00 00 00" />
                </div>
              </div>
            </div>
          </div>
          <ExpertsRattaches membreId={membre?.id} token={token} userRole={userRole} />
          <DocumentsMembreGED membreId={membre?.id} token={token} userRole={userRole} />
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-coordonnees-btn" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

